import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { HierarchyBehaviorField } from './HierarchyBehaviorField';
import { validateNestedField, validateObjectField, type ValidationResult } from '../../utils/portfolioValidation';

interface NestedConfigFieldProps {
  fieldKey: string;
  fieldData: {
    value: any;
    default_value: any;
    description: string;
    validation_rules: any;
    has_user_override: boolean;
    display_unit?: string;
    unit_type?: string;
  };
  onValueChange: (path: string, value: any) => void;
  onRemoveOverride: (path: string) => void;
  categoryKey: string;
  isUpdating?: boolean; // Add prop to track if mutation is in progress
}

export const NestedConfigField: React.FC<NestedConfigFieldProps> = ({
  fieldKey,
  fieldData,
  onValueChange,
  onRemoveOverride,
  categoryKey,
  isUpdating = false
}) => {
  // Use custom component for hierarchy_behavior field
  if (fieldKey === 'hierarchy_behavior') {
    return (
      <HierarchyBehaviorField
        fieldKey={fieldKey}
        fieldData={fieldData}
        onValueChange={onValueChange}
        onRemoveOverride={onRemoveOverride}
        categoryKey={categoryKey}
      />
    );
  }

  const { value, default_value, description, validation_rules, has_user_override, display_unit } = fieldData;

  // Local state to track input values for immediate visual feedback
  const [localInputValues, setLocalInputValues] = useState<Record<string, string>>({});
  const [activeFocusField, setActiveFocusField] = useState<string | null>(null);
  const [validationResults, setValidationResults] = useState<Record<string, ValidationResult>>({});

  // Clear local state when field changes, but be more careful about value changes
  // to avoid interfering with optimistic updates
  useEffect(() => {
    // Only clear local state if we're not actively focused on any field
    if (!activeFocusField) {
      setLocalInputValues({});
    }
  }, [fieldKey, has_user_override]); // Remove 'value' from dependencies to avoid clearing during optimistic updates

  // Helper function to format field labels
  const formatLabel = (key: string) => {
    return key
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper function to get input type and props based on validation rules
  const getInputProps = (fieldKey: string, fieldRules: any, parentRules?: any) => {
    // For nested fields, get the specific field rules from the parent's properties
    let rules = fieldRules;
    if (parentRules?.properties?.[fieldKey]) {
      rules = parentRules.properties[fieldKey];
    }
    
    const isPercent = rules?.type === 'percent';
    const min = rules?.min !== undefined ? rules.min : (isPercent ? 0 : undefined);
    const max = rules?.max !== undefined ? rules.max : (isPercent ? 100 : undefined);
    const step = isPercent ? 1 : (rules?.type === 'number' ? 0.01 : 1);

    // Determine display suffix - use display_unit if available, otherwise fallback to validation rules
    let suffix = '';
    if (display_unit) {
      suffix = display_unit;
    } else if (isPercent) {
      suffix = '%';
    }

    return {
      type: 'number',
      min,
      max,
      step,
      suffix
    };
  };

  // Helper function to check if a nested field is customized
  const isNestedFieldCustomized = (key: string) => {
    if (!has_user_override || !value || !default_value) return false;
    if (typeof value !== 'object' || typeof default_value !== 'object') return false;
    
    return value[key] !== default_value[key];
  };

  // Helper function to get the default value for a nested field
  const getNestedDefaultValue = (key: string) => {
    if (!default_value || typeof default_value !== 'object') return null;
    return default_value[key];
  };

  // Render a simple value (number, string)
  const renderSimpleField = (key: string, val: any, rules: any, fullPath: string) => {
    const inputProps = getInputProps(key, rules, validation_rules);
    const localKey = fullPath; // Use full path as the local key
    
    // Use local input value if user is actively typing in this field, otherwise use actual value
    const displayValue = (activeFocusField === localKey && localInputValues[localKey] !== undefined)
      ? localInputValues[localKey] 
      : (val !== undefined && val !== null ? val : '');
    
    // Determine if this specific field is customized (for nested fields)
    const isThisFieldCustomized = fullPath.includes('.') && fullPath !== `${categoryKey}.${fieldKey}` 
      ? isNestedFieldCustomized(key)
      : has_user_override;
    
    // Get default value for display
    const defaultVal = fullPath.includes('.') && fullPath !== `${categoryKey}.${fieldKey}`
      ? getNestedDefaultValue(key)
      : default_value;
    
    return (
      <div key={key} className="space-y-2">
        <label className="block text-sm font-medium text-white/90">
          <span className={isThisFieldCustomized ? "text-green-400" : "text-white/90"}>
            {formatLabel(key)}
          </span>
          {isThisFieldCustomized ? (
            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
              customized
            </span>
          ) : (
            <span className="ml-2 text-xs text-white/50">
              (default: {defaultVal !== undefined && defaultVal !== null ? defaultVal : 'none'})
            </span>
          )}
        </label>
        <div className="flex items-center space-x-2">
          <input
            type={inputProps.type}
            value={displayValue}
            onChange={(e) => {
              const inputValue = e.target.value;
              
              // Update local state immediately for visual feedback (only when focused)
              if (activeFocusField === localKey) {
                setLocalInputValues(prev => ({
                  ...prev,
                  [localKey]: inputValue
                }));
              }
              
              // Validate the input
              let validationResult: ValidationResult = { isValid: true };
              
              // Handle nested vs simple field updates
              if (fullPath.includes('.') && fullPath !== `${categoryKey}.${fieldKey}`) {
                // This is a nested field (object property)
                const nestedKey = key;
                const numValue = inputValue === '' || inputValue === null ? null : parseFloat(inputValue);
                
                // Validate nested field
                if (validation_rules?.properties?.[nestedKey]) {
                  validationResult = validateNestedField(nestedKey, numValue, validation_rules);
                }
                
                // Update validation state
                setValidationResults(prev => ({
                  ...prev,
                  [localKey]: validationResult
                }));
                
                const newObjectValue = { ...value, [nestedKey]: numValue };
                
                // Only trigger API call for valid numbers or empty (null)
                if (inputValue === '' || inputValue === null || !isNaN(parseFloat(inputValue))) {
                  onValueChange(`${categoryKey}.${fieldKey}`, newObjectValue);
                }
              } else {
                // This is a simple field (top-level)
                const numValue = inputValue === '' || inputValue === null ? null : parseFloat(inputValue);
                
                // Validate top-level field
                if (validation_rules) {
                  validationResult = validateObjectField(numValue, validation_rules);
                }
                
                // Update validation state
                setValidationResults(prev => ({
                  ...prev,
                  [localKey]: validationResult
                }));
                
                // Only trigger API call for valid numbers or empty (null)
                if (inputValue === '' || inputValue === null || !isNaN(parseFloat(inputValue))) {
                  onValueChange(fullPath, numValue);
                }
              }
            }}
            onFocus={() => {
              setActiveFocusField(localKey);
            }}
            onBlur={() => {
              const currentLocalValue = localInputValues[localKey];
              const currentServerValue = (val !== undefined && val !== null ? val.toString() : '');
              
              // Smart delay based on whether values match and if we're updating
              const valuesMatch = currentLocalValue === currentServerValue || !currentLocalValue;
              const baseDelay = isUpdating ? 1000 : 300; // Longer delay when updating
              const actualDelay = valuesMatch ? 100 : baseDelay; // Quick clear if values match
              
              setTimeout(() => {
                // Only clear if the field is still not focused (user hasn't refocused)
                if (activeFocusField !== localKey) {
                  setActiveFocusField(null);
                  setLocalInputValues(prev => {
                    const newState = { ...prev };
                    delete newState[localKey];
                    return newState;
                  });
                }
              }, actualDelay);
            }}
            className={`flex-1 px-3 py-2 bg-white/10 border rounded-md text-white text-sm focus:ring-2 focus:ring-blue-400/50 focus:border-transparent placeholder-white/40 ${
              validationResults[localKey] && !validationResults[localKey].isValid 
                ? 'border-red-400/60 bg-red-500/10' 
                : 'border-white/20'
            }`}
            min={inputProps.min}
            max={inputProps.max}
            step={inputProps.step}
          />
          {inputProps.suffix && (
            <span className="text-white/60 text-sm">{inputProps.suffix}</span>
          )}
          {validationResults[localKey] && !validationResults[localKey].isValid && (
            <ExclamationTriangleIcon className="w-5 h-5 text-red-400" title={validationResults[localKey].error} />
          )}
        </div>
        {validationResults[localKey] && !validationResults[localKey].isValid && (
          <div className="mt-1 text-red-400 text-xs">
            {validationResults[localKey].error}
          </div>
        )}
      </div>
    );
  };

  // Render object fields (nested configuration)
  const renderObjectFields = () => {
    if (!value || typeof value !== 'object') {
      return (
        <div className="text-center py-4 text-white/60">
          <p className="text-sm">No configuration data available</p>
        </div>
      );
    }

    const properties = validation_rules?.properties || {};
    
    return (
      <div className="space-y-4">
        {Object.entries(value).map(([key, val]) => {
          const fieldRules = properties[key];
          const fullPath = `${categoryKey}.${fieldKey}.${key}`;
          
          return renderSimpleField(key, val, fieldRules, fullPath);
        })}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="bg-white/5 rounded-lg p-4 border border-white/10 space-y-4"
    >
      {/* Field Header */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h4 className={`text-base font-medium mb-1 ${has_user_override ? 'text-green-400' : 'text-white/90'}`}>
            {formatLabel(fieldKey)}
            {has_user_override && (
              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                Customized
              </span>
            )}
          </h4>
          <p className="text-sm text-white/70 leading-relaxed break-words whitespace-pre-wrap">
            {description}
          </p>
        </div>
        
        {has_user_override && (
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={() => onRemoveOverride(`${categoryKey}.${fieldKey}`)}
              className="p-1.5 text-white/60 hover:text-red-400 transition-colors bg-white/5 hover:bg-red-500/10 rounded-md border border-white/10 hover:border-red-500/30"
              title="Reset to default values"
            >
              <ArrowPathIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Field Content */}
      <div className="pt-2">
        {typeof value === 'object' && value !== null ? (
          renderObjectFields()
        ) : (
          renderSimpleField(fieldKey, value, validation_rules, `${categoryKey}.${fieldKey}`)
        )}
      </div>
    </motion.div>
  );
};
